import { useEffect, type ReactNode } from "react";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { completeLoginTiming } from "@telemetry/loginTiming";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "react-router-dom";

interface ProtectedRouteProps {
  requiredPermissions?: string[];
  requireAll?: boolean;
  children?: ReactNode;
}

function ProtectedRoute({
  requiredPermissions = [],
  requireAll = false,
  children,
}: ProtectedRouteProps) {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const userAccessPermissions = useAuthStore((s) => s.userAccessPermissions);

  const { i18n } = useTranslation();
  const location = useLocation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  // Wait for store to have permissions if this route cares about them
  const needsPerms = requiredPermissions.length > 0;
  const permsKnown = !needsPerms || userAccessPermissions.length > 0;
  const allowed = !needsPerms
    ? true
    : requireAll
      ? requiredPermissions.every((rp) => userAccessPermissions.includes(rp))
      : requiredPermissions.some((rp) => userAccessPermissions.includes(rp));
  const currentPath = `${location.pathname}${location.search}`;
  const primaryRole = user?.roles?.[0] ?? "";
  const canRenderProtectedContent = Boolean(token) && permsKnown && allowed;

  useEffect(() => {
    if (!canRenderProtectedContent) return;

    const timer = window.setTimeout(() => {
      completeLoginTiming({
        role: primaryRole,
        completedPath: currentPath,
        completedScreen: currentPath,
      });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [canRenderProtectedContent, currentPath, primaryRole]);

  if (!token || !permsKnown) {
    return (
      <PageLayout dir={dir}>
        <div style={{ padding: 24 }}>Loading…</div>
      </PageLayout>
    );
  }

  // Permission check
  if (!allowed) {
    return (
      <PageLayout dir={dir}>
        <Navigate to={paths.unauthorized} replace state={{ from: location }} />
      </PageLayout>
    );
  }

  return <PageLayout dir={dir}>{children || <Outlet />}</PageLayout>;
}

export default ProtectedRoute;
